import {useState} from 'react'
import {documentToPlainTextString} from '@contentful/rich-text-plain-text-renderer'
import {documentToHtmlString} from '@contentful/rich-text-html-renderer'
import {resolveGenericContentCta, type GenericContent} from '../../../../../../brand/lib/types/contentful'

import {Heading, Image, Prose} from '@primer/react-brand/esm'
import {TriangleDownIcon} from '@primer/octicons-react'
import {CtaButtons} from '../../_shared/CtaButtons/CtaButtons'
import {Eyebrow} from '../../_shared/Eyebrow/Eyebrow'
import {Spacer} from '../../_shared/Spacer/Spacer'

import Styles from './RiverAccordion.module.css'

type Props = {
  content: GenericContent
  items: GenericContent[]
  location: string
  sectionIndex: number
}

export function RiverAccordion(props: Props) {
  const {content, items, location, sectionIndex} = props
  const {htmlId, heading, label} = content.fields

  const direction = htmlId || 'left' // Used for setting left/right alignment

  const title = heading
  const eyebrow = label?.fields.text
  const ctaGroup = resolveGenericContentCta(content, {legacyFallback: 'first'})

  // Accordion
  const [expandedIndex, setExpandedIndex] = useState<number>(0)
  const [previousIndex, setPreviousIndex] = useState<number | null>(null)

  const handleExpandItem = (itemIndex: number) => {
    setPreviousIndex(expandedIndex)
    setExpandedIndex(itemIndex)
  }

  return (
    <div className={Styles.RiverAccordion} data-direction={direction}>
      <div className={Styles.Cell}>
        <div className={Styles.Content}>
          {eyebrow && (
            <>
              <Eyebrow text={eyebrow} />
              <Spacer size="64px" size1280="0" />
            </>
          )}

          {title && (
            <>
              <Heading data-heading="2" as={sectionIndex === 0 ? 'h2' : 'h3'}>
                {title}
              </Heading>
              <Spacer size="16px" size1280="0" />
            </>
          )}

          {/* Accordion */}
          <div>
            {items
              .filter(item => {
                const {subheading, text} = item.fields
                const summary = subheading ? documentToPlainTextString(subheading) : ''
                const description = text ? documentToPlainTextString(text) : ''
                return summary && description
              })
              .map((item, itemIndex) => {
                // Accordion item
                const {subheading, text} = item.fields
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                const summary = documentToPlainTextString(subheading!)
                if (!text) return null
                const description = documentToHtmlString(text)
                const imageAlt = item.fields.media?.[0]?.fields?.asset?.fields?.description || ''

                const isExpanded = expandedIndex === itemIndex

                return (
                  <div className={Styles.AccordionItem} key={item.sys?.id || itemIndex} data-expanded={isExpanded}>
                    <button
                      className={Styles.AccordionSummary}
                      onClick={() => handleExpandItem(itemIndex)}
                      aria-expanded={isExpanded}
                    >
                      {summary}
                      <TriangleDownIcon className={Styles.AccordionIcon} />
                    </button>

                    <div
                      className={Styles.AccordionContainer}
                      // [a11y] Indicates this element and all its descendants are inert (non-interactive) when not expanded
                      {...({inert: isExpanded ? undefined : 'true'} as React.HTMLAttributes<HTMLDivElement>)}
                    >
                      <div className={Styles.AccordionContent}>
                        <Prose className={Styles.Description} html={description} />

                        {/* A11y: Read image alt text */}
                        {imageAlt && <span className="sr-only">{imageAlt}</span>}
                      </div>
                    </div>
                  </div>
                )
              })}
          </div>

          {ctaGroup && (
            <>
              <Spacer size="64px" size1280="0" />
              <div>
                <CtaButtons contentfulContent={ctaGroup} location={location} />
              </div>
            </>
          )}
        </div>
      </div>

      <div className={Styles.Cell}>
        {items.length > 0 && (
          <div>
            <div>
              {items.map((item, itemIndex) => {
                const image = item.fields.media?.[0]?.fields
                if (!image?.asset.fields.file.details?.image) return null // Only allow images

                return (
                  <Image
                    className={Styles.Image}
                    key={item.sys?.id || itemIndex}
                    src={image.asset.fields.file.url}
                    width={image.width || image.asset.fields.file.details?.image?.width || ''}
                    height={image.height || image.asset.fields.file.details?.image?.height || ''}
                    data-current={itemIndex === expandedIndex}
                    data-last={itemIndex === previousIndex}
                    alt=""
                    loading="lazy"
                  />
                )
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
